import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import { apiService } from '../../components/Config/api.service';
import SecondarySidebar from '../../components/SideBar/SecondarySidebar';
import PrimeImposable from './Form/PrimeImposable';
import PrimeNonImposable from './Form/PrimeNonImposable';
import Avantages from './Form/PrimeAvantages';
import NoteFrais from './Form/NoteFrais';
import { notify } from '../../components/Utils/Notifications';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import DeductionForm from './Form/DeductionForm';
import ParametresAvances from './ParametresAvances';
import { FaArchive, FaCoins, FaStickyNote, FaUsers, FaChartLine, FaGift, FaUndo } from 'react-icons/fa';
import { BiCreditCard } from 'react-icons/bi';
import { formatMoney } from '../../components/Utils/FormatUtils';
import { ongletsDe } from '../../components/Navigation/navigation';
import { getStoredUser } from '../../components/Auth/permissions';
import useOnglet from '../../components/Navigation/useOnglet';

function Primes() {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
    const [filterBy, setFilterBy] = useState('');
    // Les onglets viennent de la carte de navigation, et l'onglet demandé
    // vit dans l'adresse (Yros/Kavero#416). L'identité stockée, et non
    // `null` : l'onglet des avances est gardé par l'offre (YRH-FOUND-014), et
    // un onglet gardé reste fermé tant qu'aucun utilisateur n'est donné.
    const menuItems = ongletsDe('/primes', getStoredUser(), t);
    const [ongletDemande, choisirOnglet] = useOnglet(menuItems);
    // Un onglet ne change pas en cours de saisie, d'où qu'on le demande :
    // l'onglet de l'écran, la barre latérale, la recherche ou le retour du
    // navigateur. Sans ce verrou, le formulaire d'un autre onglet s'ouvrirait
    // sur l'élément en cours de saisie, et l'enregistrer écrirait au mauvais
    // endroit.
    const [edit, setEdit] = useState(false);
    const [ongletEnSaisie, setOngletEnSaisie] = useState(null);
    const activeTab = edit ? ongletEnSaisie : ongletDemande;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 25,
        total_pages: 1,
        total_count: 0
    });

    const [stats, setStats] = useState({
        total_primes: 0,
        total_advantages: 0,
        total_loans: 0,
        total_notes: 0,
        total_employees: 0
    });

    const columns = activeTab === "notes" ? [
        { key: 'employee_name', label: t('primesPage.employee') },
        { key: 'name', label: t('primesPage.reason') },
        { key: 'date', label: t('primesPage.date') },
        { key: 'amount', label: t('primesPage.amount'), render: (item) => formatMoney(item.amount) }
    ] : activeTab === "pimp" ? [
        { key: 'name', label: t('primesPage.name') },
        {
            key: 'categoryCode',
            label: t('primesPage.valueType'),
            render: (item) => {
                const categoryMap = {
                    'BRUT': t('primesPage.gross'),
                    'CBP': t('primesPage.grossProrated'),
                    'PN': t('primesPage.net'),
                    'PANEX': t('primesPage.netAlreadyReceived'),
                    'PNP': t('primesPage.netProrated')
                };
                return categoryMap[item.categoryCode] || item.categoryCode;
            }
        },
        {
            key: 'proratise',
            label: t('primesPage.prorated'),
            render: (item) => {
                const proratiseCategories = ['CBP', 'PNP'];
                return proratiseCategories.includes(item.categoryCode) ? t('primesPage.yes') : t('primesPage.no');
            }
        },
        { key: 'employee_count', label: t('primesPage.employeesConcerned') },
        { key: 'amount', label: t('primesPage.totalAmount'), render: (item) => formatMoney(item.amount) },
    ] : activeTab === "pnoimp" ? [
        { key: 'name', label: t('primesPage.name') },
        {
            key: 'affichage',
            label: t('primesPage.display'),
            render: (item) => {
                return item.categoryCode === 'CBNPNI' ? t('primesPage.grossLevel') : t('primesPage.netLevel');
            }
        },
        { key: 'employee_count', label: t('primesPage.employeesConcerned') },
        { key: 'amount', label: t('primesPage.totalAmount'), render: (item) => formatMoney(item.amount) },
    ] : [
        { key: 'name', label: t('primesPage.name') },
        {
            key: 'category',
            label: t('primesPage.valueType'),
            render: (item) => item.categoryCode === 'Brut' ? t('primesPage.gross') : t('primesPage.net')
        },
        { key: 'employee_count', label: t('primesPage.employeesConcerned') },
        { key: 'amount', label: t('primesPage.totalAmount'), render: (item) => formatMoney(item.amount) },
    ];


    const newElement = {
        name: "",
        category: "",
        valeur: 0,
        position: 1,
        employees_primes: [],
        apply_selected: false
    }

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [element, setElement] = useState(newElement);

    const getEndpointConfig = useCallback((category, archived = showArchived) => {
        let endpoint = archived ? '/primes/archived' : '/primes';
        let responseKey = 'primes';
        let tax = 'imp';

        switch (category) {
            case "pimp":
                tax = 'imp';
                break;
            case "pnoimp":
                tax = 'noimp';
                break;
            case "advantages":
                endpoint = archived ? '/advantages/archived' : '/advantages';
                responseKey = 'advantages';
                tax = null;
                break;
            case "notes":
                endpoint = archived ? '/notes/archived' : '/notes';
                responseKey = 'notes';
                tax = null;
                break;
            case "deduction":
                endpoint = archived ? '/loans/archived' : '/loans';
                responseKey = 'loans';
                tax = null;
                break;
            default:
                break;
        }

        return { endpoint, responseKey, tax };
    }, [showArchived]);

    const getUnarchiveEndpoint = (itemId) => {
        switch (activeTab) {
            case "advantages":
                return `/advantage/${itemId}/unarchive`;
            case "notes":
                return `/note/${itemId}/unarchive`;
            case "deduction":
                return `/loan/${itemId}/unarchive`;
            default:
                return `/prime/${itemId}/unarchive`;
        }
    };

    const fetchPrimes = useCallback(
        (page = 1, search = '', filter = '', category = activeTab) => {
            setLoading(true);

            const offset = (page - 1) * pagination.limit;
            const params = new URLSearchParams();
            params.append('offset', offset);
            params.append('limit', pagination.limit);
            if (search) {
                params.append('search', search);
            }
            if (filter) {
                params.append('filter_by', JSON.stringify(filter));
            }

            const { endpoint, responseKey, tax } = getEndpointConfig(category);
            if (tax) {
                params.append('tax', tax);
            }

            apiService.getElement(`${endpoint}?${params.toString()}`)
                .then(response => {
                    const responseData = response.data;
                    setData(responseData[responseKey] || []);

                    if (responseData.pagination) {
                        const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;

                        setPagination({
                            ...responseData.pagination,
                            page: currentPage
                        });
                    }
                    setLoading(false);
                })
                .catch(error => {
                    console.error(`There was an error fetching data from ${endpoint}:`, error);
                    setData([]);
                    setPagination({
                        page: 1,
                        limit: 25,
                        total_pages: 1,
                        total_count: 0
                    });
                    setLoading(false);
                });
        },
        [activeTab, getEndpointConfig, pagination.limit]
    );
    
    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;
    
        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        if (newFilterBy !== filterBy) {
            setFilterBy(newFilterBy);
        }
    
        fetchPrimes(page, newSearchTerm, newFilterBy, activeTab);
    };

    const fetchStats = () => {
        apiService.getElement('/primes/stats')
            .then(response => {
                const responseData = response.data;
                if (responseData.stats) {
                    setStats({
                        total_primes: responseData.stats.total_primes_amount || 0,
                        total_advantages: responseData.stats.total_advantages_amount || 0,
                        total_loans: responseData.stats.total_loans_amount || 0,
                        total_notes: responseData.stats.total_expense_notes_amount || 0,
                        total_employees: responseData.stats.total_employees || 0
                    });
                }
            })
            .catch(error => {
                console.error("There was an error fetching stats:", error);
            });
    };


    useEffect(() => {
        // L'onglet des bornes n'a pas de liste à charger : il lit ses valeurs
        // lui-même, et demander des « primes » de type « avances » au serveur
        // n'aurait aucun sens.
        !edit && activeTab !== "avances" && fetchPrimes(1, searchTerm, filterBy, activeTab);
        fetchStats();
    }, [activeTab, edit, filterBy, searchTerm,  fetchPrimes]);

    



    const changeTab = (tab) => {
        if (edit) {
            notify(t('primesPage.saveOrCancelChanges'), '', 4)
        } else {
            closeEdit();
            choisirOnglet(tab);
        }
    }

    // Même refus quand l'adresse change sans passer par les onglets de
    // l'écran : l'adresse revient à l'onglet de la saisie.
    useEffect(() => {
        if (edit && ongletDemande !== ongletEnSaisie) {
            notify(t('primesPage.saveOrCancelChanges'), '', 4);
            choisirOnglet(ongletEnSaisie, { replace: true });
        }
    }, [edit, ongletDemande, ongletEnSaisie, choisirOnglet, t]);

    const toggleArchivedView = () => {
        if (edit) {
            notify(t('primesPage.saveOrCancelChanges'), '', 4);
            return;
        }
        setData([]);
        setPagination({
            offset: 0,
            limit: pagination.limit,
            total_pages: 1,
            total_count: 0,
            page: 1
        });
        setShowArchived(prev => !prev);
    };

    const handleUnarchive = async (item, event) => {
        event.stopPropagation();

        const confirmed = window.confirm(t('primesPage.unarchiveConfirm', { name: item.name }));
        if (!confirmed) {
            return;
        }

        try {
            setLoading(true);
            await apiService.updateElement(getUnarchiveEndpoint(item.id), {});
            notify(t('primesPage.unarchiveSuccess'), '', 2);
            fetchPrimes(pagination.page || 1, searchTerm, filterBy, activeTab);
            fetchStats();
        } catch (error) {
            console.error("Erreur lors du désarchivage:", error);
            notify(t('primesPage.unarchiveError'), '', 3);
            setLoading(false);
        }
    };


    const closeEdit = () => {
        setEdit(false);
        setElement(newElement);
    }

    const editPrime = (element = newElement) => {
        setElement(element)
        setOngletEnSaisie(activeTab);
        setEdit(true);
    }



    const renderContent = () => {
        switch (activeTab) {
            case "pnoimp":
                return (
                    <PrimeNonImposable
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                    />
                );
            case "advantages":
                return <Avantages 
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                        />
            case "notes":
                return <NoteFrais 
                        id={element?.id}
                        note={element}
                        setNote={setElement}
                        setErrorMessage={setErrorMessage}
                        setSuccessMessage={setSuccessMessage}
                        closeEdit={closeEdit}
                    />;
            case "deduction":
                return (
                    <DeductionForm
                        id={element?.id}
                        deduction={element}
                        setDeduction={setElement}
                        closeEdit={closeEdit}
                    />
                )

            default:
                return (
                    <PrimeImposable
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                    />
                );
        }
    };
    const formatNumber = (value) => {
        return new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'XOF' }).format(value);
    };

    // Détermine la valeur du model selon activeTab
    const getModel = () => {
        if (activeTab === "pimp" || activeTab === "pnoimp") {
            return "prime";
        }
        return activeTab;
    };

    const getHeaderContent = () => {
        switch (activeTab) {
            case "pimp":
                return {
                    title: t('primesPage.taxablePrimesTitle'),
                    text: showArchived ? t('primesPage.taxablePrimesArchivedText') : t('primesPage.taxablePrimesText'),
                    ajout: t('primesPage.primeAdd')
                };
            case "pnoimp":
                return {
                    title: t('primesPage.nonTaxablePrimesTitle'),
                    text: showArchived ? t('primesPage.nonTaxablePrimesArchivedText') : t('primesPage.nonTaxablePrimesText'),
                    ajout: t('primesPage.primeAdd')
                };
            case "advantages":
                return {
                    title: t('primesPage.advantagesTitle'),
                    text: showArchived ? t('primesPage.advantagesArchivedText') : t('primesPage.advantagesText'),
                    ajout: t('primesPage.advantageAdd')
                };
            case "notes":
                return {
                    title: t('primesPage.notesTitle'),
                    text: showArchived ? t('primesPage.notesArchivedText') : t('primesPage.notesText'),
                    ajout: t('primesPage.noteAdd')
                };
            case "deduction":
                return {
                    title: t('primesPage.deductionsTitle'),
                    text: showArchived ? t('primesPage.deductionsArchivedText') : t('primesPage.deductionsText'),
                    ajout: t('primesPage.deductionAdd')
                };
            default:
                return {
                    title: t('primesPage.defaultTitle'),
                    text: t('primesPage.defaultText'),
                    ajout: t('primesPage.primeAddShort')
                };
        }
    };

    const renderArchivedActions = (row) => (
        <button
            type="button"
            onClick={(event) => handleUnarchive(row, event)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[--color-primary] border border-[--color-primary] rounded-lg hover:bg-[--color-primary] hover:text-white transition-colors duration-200"
        >
            <FaUndo size={12} />
            {t('primesPage.unarchiveButton')}
        </button>
    );

    const renderArchiveViewButton = () => (
        <button
            type="button"
            onClick={toggleArchivedView}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-200 ${
                showArchived
                    ? 'bg-[--color-primary] text-white border-[--color-primary] hover:bg-white hover:text-[--color-primary]'
                    : 'bg-white text-[--color-primary] border-[--color-primary] hover:bg-[--color-primary] hover:text-white'
            }`}
        >
            <FaArchive size={14} />
            {showArchived ? t('employeesPage.viewActive') : t('employeesPage.viewArchived')}
        </button>
    );

   const getStatsIcon = (type) => {
        switch (type) {
            case 'primes': return <FaCoins className="text-white" size={24} />;
            case 'advantages': return <FaGift className="text-white" size={24} />;
            case 'deductions': return <BiCreditCard className="text-white" size={24} />;
            case 'notes': return <FaStickyNote className="text-white" size={24} />;
            case 'employees': return <FaUsers className="text-white" size={24} />;
            default: return <FaChartLine className="text-white" size={24} />;
        }
    };

    return (
        <div>
            <HeaderCard
                title={t('primesPage.pageTitle')}
                text={t('primesPage.pageSubtitle')}
            />
            {/* Statistiques - Restent en haut sur toute la largeur */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    {
                        title: t('primesPage.statPrimes'),
                        value: formatNumber(stats.total_primes),
                        type: 'primes',
                        subtitle: t('primesPage.statPrimesSubtitle')
                    },
                    {
                        title: t('primesPage.statAdvantages'),
                        value: formatNumber(stats.total_advantages),
                        type: 'advantages',
                        subtitle: t('primesPage.statAdvantagesSubtitle')
                    },
                    {
                        title: t('primesPage.statDeductions'),
                        value: formatNumber(stats.total_loans),
                        type: 'deductions',
                        subtitle: t('primesPage.statDeductionsSubtitle')
                    },
                    {
                        title: t('primesPage.statNotes'),
                        value: formatNumber(stats.total_notes),
                        type: 'notes',
                        subtitle: t('primesPage.statNotesSubtitle')
                    }
                ].map(({ title, value, type, subtitle }) => (
                    <div
                        key={title}
                        className={`bg-gradient-to-r bg-white rounded-lg p-6    hover:shadow-md transition-shadow duration-300`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className=" text-sm font-medium uppercase tracking-wide">
                                    {title}
                                </p>
                                <p className="text-xl font-bold mt-2">{value}</p>
                                <p className="text-xs mt-1">{subtitle}</p>
                            </div>
                            <div className="bg-[--color-primary] rounded-full p-3">
                                {getStatsIcon(type)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex gap-4 py-4 ">
                {/* Sidebar */}
                <div className="w-80 flex-shrink-0 self-start sticky top-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <h3 className="text-lg font-semibold text-gray-800">{t('primesPage.remunerationTypes')}</h3>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{t('primesPage.remunerationTypesSubtitle')}</p>
                        </div>
                        <div className="p-2">
                            <SecondarySidebar 
                                activeTab={activeTab} 
                                setActiveTab={changeTab} 
                                menuItems={menuItems} 
                            />
                        </div>
                    </div>
                </div>
                <div className=" bg-white rounded-lg shadow-md flex-1 min-w-0">
                    {loading && <LoadingBarWithStyles />}
                    <div className='p-8'>
                        {activeTab === "avances" ? <ParametresAvances /> : (
                        <div className="container mx-auto ">
                            <div className="">
                                {!edit && (
                                    <HeaderCard
                                        title={showArchived ? t('primesPage.titleArchivedSuffix', { title: getHeaderContent().title }) : getHeaderContent().title}
                                        text={getHeaderContent().text}
                                        ajout={showArchived ? null : getHeaderContent().ajout}
                                        addNew={showArchived ? undefined : editPrime}
                                        rightActions={renderArchiveViewButton()}
                                    />
                                )}
                                {!edit && (
                                    <div className="text-sm text-gray-600 mb-4">
                                        {showArchived
                                            ? t('primesPage.archivedCount', { count: pagination.total_count || 0 })
                                            : t('primesPage.activeCount', { count: pagination.total_count || 0 })}
                                    </div>
                                )}
                            </div>
                            <div className="">
                                {edit ?
                                    renderContent() :
                                    <TableList
                                        columns={columns}
                                        data={data}
                                        onRowClick={showArchived ? undefined : editPrime}
                                        pagination={pagination}
                                        onPageChange={handlePageChange}
                                        searchQuery={searchTerm}
                                        setSearchQuery={setSearchTerm}
                                        // setFilterBy={setFilterBy}
                                        // filterBy={filterBy}
                                        showFilter={false}
                                        model={getModel()}
                                        loading={loading}
                                        extraActions={showArchived ? renderArchivedActions : undefined}
                                    />
                                }
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Primes;
